/**
 * AgentModePanel — the "🤖 Agent" tab of the GRC Copilot sidebar.
 *
 * The user types a GOAL → we call the F8 agentic tool-planner
 * (`POST /ai/copilot/agent/plan`, passing the current CrudPage's module +
 * field schema as `available_forms`) → render the returned PLAN as an ordered
 * step list + the assistant message → "Lancer" EXECUTES it step-by-step:
 *
 *   navigate       → react-router push
 *   fillForm       → open the current CrudPage create dialog pre-filled (preview)
 *   search         → copilot semantic search
 *   findSimilar    → vector-similar records
 *   analyze        → grounded RAG Q&A
 *   suggestLinks   → link suggestions
 *   generateRecord → full record draft (fields pre-fill the create dialog)
 *   createRecord   → POST to the module's create endpoint            [WRITE]
 *   linkRecords    → POST a relationship                              [WRITE]
 *
 * `{{stepN.field}}` placeholders in later args are resolved from prior step
 * outputs. Read ops auto-run; every WRITE step (and any `confirm:true` step) is
 * CONFIRM-GATED — an inline card shows the proposed values and the user must
 * approve before it runs.
 */

import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Bot, Wand2, Loader2, Play, RotateCcw, Check, X, AlertTriangle,
  Compass, Search as SearchIcon, GitCompareArrows, Sparkles, FilePlus2,
  Link2, ShieldCheck, CircleDashed, ArrowRight, ClipboardPen,
} from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'
import apiClient from '@/lib/api/client'
import {
  useAgentPlan,
  copilotApi,
  copilotApiV2,
  type AgentPlanStep,
  type AgentPlanResult,
} from '@/services/copilot.service'
import { defaultLinkTarget, INDEXED_ENTITY_TYPES } from '@/lib/copilot/entity-type-map'
import {
  getCreateEntryByEntityType,
  getCreateEntryByModule,
  applyCreateFieldMap,
} from '@/lib/copilot/create-registry'
import { useCopilotAgentStore } from '@/stores/copilot-agent.store'

export interface AgentModePanelProps {
  /** Current module slug (first route segment) for planner grounding. */
  module: string
  pageContext?: string
}

type StepStatus = 'pending' | 'running' | 'confirm' | 'done' | 'skipped' | 'error'
interface StepState {
  status: StepStatus
  error?: string
}

const WRITE_OPS = new Set(['createRecord', 'linkRecords'])
/**
 * READ / preview ops — safe to auto-run and, crucially, safe to FAIL: a read
 * error (e.g. findSimilar 404 because the entity type isn't indexed) must be
 * marked skipped and the plan must CONTINUE to the next (confirm-gated) step.
 */
const READ_OPS = new Set(['search', 'findSimilar', 'analyze', 'suggestLinks', 'generateRecord'])

/** Is this entity type actually embedded in the knowledge index? */
function isIndexedType(t: string | null | undefined): boolean {
  return !!t && (INDEXED_ENTITY_TYPES as readonly string[]).includes(t)
}

const OP_META: Record<string, { label: string; icon: React.ComponentType<{ className?: string }> }> = {
  navigate: { label: 'Naviguer', icon: Compass },
  fillForm: { label: 'Pré-remplir le formulaire', icon: ClipboardPen },
  search: { label: 'Rechercher', icon: SearchIcon },
  findSimilar: { label: 'Trouver des similaires', icon: GitCompareArrows },
  analyze: { label: 'Analyser', icon: Sparkles },
  suggestLinks: { label: 'Suggérer des liens', icon: Link2 },
  generateRecord: { label: 'Générer un enregistrement', icon: Wand2 },
  createRecord: { label: 'Créer l’enregistrement', icon: FilePlus2 },
  linkRecords: { label: 'Lier des enregistrements', icon: Link2 },
}

function opMeta(op: string) {
  return OP_META[op] ?? { label: op, icon: CircleDashed }
}

// ── {{stepN.field}} resolution ────────────────────────────────────────────────

function getPath(obj: any, path: string): unknown {
  return path.split('.').reduce<any>((acc, k) => (acc == null ? undefined : acc[k]), obj)
}

function resolveTemplates(value: any, outputs: Record<number, any>): any {
  if (typeof value === 'string') {
    return value.replace(/\{\{\s*step(\d+)\.([\w.]+)\s*\}\}/g, (_m, n: string, path: string) => {
      const v = getPath(outputs[Number(n)], path)
      if (v == null) return ''
      return typeof v === 'object' ? JSON.stringify(v) : String(v)
    })
  }
  if (Array.isArray(value)) return value.map((v) => resolveTemplates(v, outputs))
  if (value && typeof value === 'object') {
    const out: Record<string, any> = {}
    for (const k of Object.keys(value)) out[k] = resolveTemplates(value[k], outputs)
    return out
  }
  return value
}

function unwrap(resp: any): any {
  return resp && typeof resp === 'object' && 'data' in resp && resp.data && typeof resp.data === 'object'
    ? resp.data
    : resp
}

/** Extract a values-bag from step args regardless of the backend key used. */
function argValues(args: Record<string, any>): Record<string, unknown> {
  const v = args.values ?? args.fields ?? args.record ?? args.data ?? args.payload
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {}
}

// ── Component ──────────────────────────────────────────────────────────────────

export function AgentModePanel({ module, pageContext }: AgentModePanelProps) {
  const navigate = useNavigate()
  const surface = useCopilotAgentStore((s) => s.surface)
  const planM = useAgentPlan()

  const [goal, setGoal] = React.useState('')
  const [result, setResult] = React.useState<AgentPlanResult | null>(null)
  const [statuses, setStatuses] = React.useState<Record<number, StepState>>({})
  const [executing, setExecuting] = React.useState(false)
  const [ranOnce, setRanOnce] = React.useState(false)
  const [confirm, setConfirm] = React.useState<{ step: AgentPlanStep; args: Record<string, any> } | null>(null)

  const outputsRef = React.useRef<Record<number, any>>({})
  const confirmResolver = React.useRef<((ok: boolean) => void) | null>(null)

  const availableForms = React.useMemo(() => {
    if (!surface) return []
    return [
      {
        form_id: surface.module,
        entity_type: surface.entityType,
        fields: surface.fields.map((f) => ({
          name: f.name,
          label: f.label,
          type: f.type,
          ...(f.options && f.options.length ? { enum: f.options } : {}),
        })),
      },
    ]
  }, [surface])

  const setStepStatus = React.useCallback((step: number, status: StepStatus, error?: string) => {
    setStatuses((prev) => ({ ...prev, [step]: { status, error } }))
  }, [])

  const handlePlan = async () => {
    const g = goal.trim()
    if (!g) return
    setResult(null)
    setStatuses({})
    setRanOnce(false)
    outputsRef.current = {}
    try {
      const res = await planM.mutateAsync({
        goal: g,
        module,
        available_forms: availableForms,
        max_steps: 8,
      })
      setResult(res)
      const init: Record<number, StepState> = {}
      res.plan.forEach((s) => (init[s.step] = { status: 'pending' }))
      setStatuses(init)
      if (res.plan.length === 0) {
        toast.message('Aucune action planifiée', { description: res.assistant_message || undefined })
      }
    } catch {
      toast.error('La planification de l’agent a échoué. Reformulez votre objectif.')
    }
  }

  // ── Confirm gate ──────────────────────────────────────────────────────────
  const awaitConfirm = (step: AgentPlanStep, args: Record<string, any>) => {
    setConfirm({ step, args })
    return new Promise<boolean>((resolve) => {
      confirmResolver.current = resolve
    })
  }
  const decideConfirm = (ok: boolean) => {
    setConfirm(null)
    confirmResolver.current?.(ok)
    confirmResolver.current = null
  }

  // ── Open a CrudPage create surface pre-filled (fillForm / generateRecord) ──
  const openCreateSurface = async (
    targetModule: string,
    prefill?: Record<string, unknown>,
    prompt?: string,
  ) => {
    const cur = useCopilotAgentStore.getState().surface
    if (cur && cur.module === targetModule) {
      cur.open(prefill)
      if (prompt) {
        try {
          const res = await copilotApi.fillForm({ form_schema: cur.fields, prompt, module: targetModule })
          cur.applyPrefill(res.fields)
        } catch {
          /* leave the direct prefill in place */
        }
      }
      return
    }
    // Target not mounted → queue a one-shot request and navigate there.
    useCopilotAgentStore.getState().requestCreate({ module: targetModule, prefill, prompt })
    navigate(`/${targetModule}`)
  }

  // ── Single-step executor (args already template-resolved) ─────────────────
  const executeStep = async (step: AgentPlanStep, args: Record<string, any>): Promise<any> => {
    const cur = () => useCopilotAgentStore.getState().surface
    switch (step.op) {
      case 'navigate': {
        const to = args.to ?? args.path ?? args.route ?? args.url
        if (to) navigate(String(to))
        return { to }
      }
      case 'search': {
        const q = args.query ?? args.q ?? args.text ?? args.keywords ?? ''
        const types = args.types ?? (args.entity_type ? [args.entity_type] : undefined)
        const hits = await copilotApi.searchEntities(String(q), types)
        return { results: hits, count: hits.length, first: hits[0] ?? null }
      }
      case 'findSimilar': {
        const et = args.entity_type ?? args.entityType ?? cur()?.entityType
        const id = args.entity_id ?? args.entityId ?? args.id
        // Degrade gracefully: a missing id, a non-indexed type, or a 404 =
        // "aucun élément similaire" (informational), never a fatal step.
        if (!et || !id) {
          return { results: [], count: 0, first: null, note: 'aucun élément similaire' }
        }
        try {
          const hits = await copilotApi.similarEntities(String(et), String(id))
          return { results: hits, count: hits.length, first: hits[0] ?? null }
        } catch {
          return { results: [], count: 0, first: null, note: 'aucun élément similaire' }
        }
      }
      case 'analyze': {
        const question = args.question ?? args.query ?? args.prompt ?? args.text ?? ''
        const res = await copilotApi.analyzeEntities({
          question: String(question),
          types: args.types,
          entity_type: args.entity_type,
          entity_id: args.entity_id,
        })
        return { answer: res.answer, grounded: res.grounded, citations: res.citations }
      }
      case 'suggestLinks': {
        const et = args.entity_type ?? args.entityType ?? cur()?.entityType ?? ''
        const res = await copilotApiV2.suggestLinks({
          entity_type: String(et),
          entity_id: String(args.entity_id ?? args.entityId ?? args.id ?? ''),
          target_type: String(args.target_type ?? args.targetType ?? defaultLinkTarget(et)),
          module: args.module ?? module,
          with_rationale: true,
        })
        return { suggestions: res.suggestions, count: res.suggestions.length, first: res.suggestions[0] ?? null }
      }
      case 'generateRecord': {
        const s = cur()
        const res = await copilotApiV2.generateRecord({
          entity_type: String(args.entity_type ?? s?.entityType ?? module),
          module: String(args.module ?? s?.module ?? module),
          prompt: String(args.prompt ?? args.description ?? goal),
          form_schema: (s?.fields ?? []).map((f) => ({
            name: f.name,
            label: f.label,
            type: f.type ?? 'text',
            required: !!f.required,
            ...(f.options && f.options.length ? { options: f.options } : {}),
          })),
          board_grade: Boolean(args.board_grade),
        })
        // Pre-fill the create dialog with the generated fields (preview).
        if (s && Object.keys(res.fields || {}).length > 0) {
          s.open(res.fields)
        }
        return { fields: res.fields, body_markdown: res.body_markdown, grounded: res.grounded }
      }
      case 'fillForm': {
        const targetModule = String(args.module ?? cur()?.module ?? module)
        const values = argValues(args)
        const prompt = args.prompt ? String(args.prompt) : undefined
        await openCreateSurface(targetModule, values, prompt)
        return { module: targetModule, values }
      }
      case 'createRecord': {
        const s = cur()
        // Resolve the create entry by planner entity_type (or the mounted
        // surface / current module) so we can (a) fall back to a static create
        // endpoint when NO surface is registered — the 3 MAIN custom pages —
        // and (b) apply the entity's field map (title→name for a risk, etc.) so
        // nothing is dropped or POSTed under the wrong backend column.
        const entityType = String(args.entity_type ?? args.entityType ?? s?.entityType ?? module)
        const entry =
          getCreateEntryByEntityType(entityType) ?? getCreateEntryByModule(s?.module ?? module)
        const endpoint = args.endpoint ?? args.create_endpoint ?? s?.createEndpoint ?? entry?.createPath
        if (!endpoint) throw new Error('Aucun point de création disponible pour ce module')
        const payload = applyCreateFieldMap(entry, { ...argValues(args) })
        const resp = await apiClient.post(String(endpoint), payload)
        const saved = unwrap(resp)
        return { id: saved?.id, record: saved }
      }
      case 'linkRecords': {
        const s = cur()
        // The durable typed-link endpoint is
        //   POST /<module>/<collection>/{sourceId}/links  (e.g. /risk-pro/risks/{risk_id}/links)
        // The SOURCE id belongs in the PATH; the body is {link_type, target_ref|target_id}.
        // (Previously this posted to no id / was a no-op → the copilot link step 405'd.)
        let endpoint = (args.endpoint ?? args.link_endpoint) as string | undefined
        const sourceRef = String(
          args.source_ref ?? args.source_id ?? args.sourceRef ?? args.sourceId ??
            args.risk_id ?? args.riskId ?? args.entity_id ?? args.entityId ?? args.id ?? '',
        )
        if (!endpoint && sourceRef) {
          // Prefer the mounted surface's create endpoint; otherwise resolve a
          // static base by the SOURCE entity type / module (the 3 MAIN custom
          // pages register no surface). The durable typed-link endpoint is
          // `${createPath}/{sourceId}/links` (e.g. /risk-pro/risks/{id}/links).
          const sourceType = String(
            args.entity_type ?? args.entityType ?? args.source_type ?? args.sourceType ?? s?.entityType ?? module,
          )
          const entry =
            getCreateEntryByEntityType(sourceType) ?? getCreateEntryByModule(s?.module ?? module)
          const base = s?.createEndpoint ?? entry?.createPath
          if (base) {
            endpoint = `${String(base).replace(/\/$/, '')}/${encodeURIComponent(sourceRef)}/links`
          }
        }
        if (!endpoint) {
          throw new Error('linkRecords : endpoint de lien introuvable (source ou surface manquante)')
        }
        const linkType = String(
          args.link_type ?? args.linkType ?? args.type ?? args.target_type ?? args.targetType ?? 'CONTROL',
        ).toUpperCase()
        const targetRef = args.target_ref ?? args.targetRef ?? args.target_code ?? args.targetCode ?? args.target
        const targetId = args.target_id ?? args.targetId
        const body: Record<string, unknown> = {
          ...(args.payload && typeof args.payload === 'object' ? (args.payload as Record<string, unknown>) : {}),
          link_type: linkType,
          ...(targetRef ? { target_ref: String(targetRef) } : {}),
          ...(targetId ? { target_id: String(targetId) } : {}),
        }
        const resp = await apiClient.post(String(endpoint), body)
        return unwrap(resp)
      }
      default:
        return null
    }
  }

  // ── Run the whole plan ────────────────────────────────────────────────────
  const runPlan = async () => {
    if (!result || executing) return
    setExecuting(true)
    setRanOnce(true)
    const outputs = (outputsRef.current = {})
    for (const step of result.plan) {
      setStepStatus(step.step, 'running')
      // `{{stepN.field}}` from a prior SKIPPED/failed step resolves to '' here
      // (resolveTemplates → getPath on undefined → '') so later steps never crash.
      const args = resolveTemplates(step.args ?? {}, outputs)
      const isWrite = WRITE_OPS.has(step.op)

      // findSimilar on a NON-indexed entity type can never resolve → skip it
      // informatively instead of firing a doomed 404 that used to halt the plan.
      if (step.op === 'findSimilar') {
        const et = String(
          args.entity_type ??
            args.entityType ??
            useCopilotAgentStore.getState().surface?.entityType ??
            '',
        )
        if (et && !isIndexedType(et)) {
          outputs[step.step] = { results: [], count: 0, first: null, note: 'type non indexé' }
          setStepStatus(step.step, 'skipped', `Type « ${et} » non indexé — aucun élément similaire`)
          continue
        }
      }

      // WRITE ops (and any confirm:true step) stay behind the confirm gate.
      const shouldConfirm = step.confirm === true || isWrite
      if (shouldConfirm) {
        setStepStatus(step.step, 'confirm')
        const ok = await awaitConfirm(step, args)
        if (!ok) {
          setStepStatus(step.step, 'skipped', 'Ignorée par l’utilisateur')
          continue
        }
        setStepStatus(step.step, 'running')
      }

      try {
        const out = await executeStep(step, args)
        outputs[step.step] = out
        // A findSimilar that yielded nothing degrades to an informational skip.
        if (
          step.op === 'findSimilar' &&
          out && typeof out === 'object' && (out as any).count === 0
        ) {
          setStepStatus(step.step, 'skipped', 'Aucun élément similaire')
        } else {
          setStepStatus(step.step, 'done')
        }
      } catch (e: any) {
        const msg = e?.message ?? 'Erreur d’exécution'
        if (isWrite) {
          // WRITE failure: surface the error but DO NOT halt — the user keeps
          // the confirm gate and can retry / let the plan continue.
          outputs[step.step] = { error: msg }
          setStepStatus(step.step, 'error', msg)
          toast.error(`Étape ${step.step} (écriture) échouée — ${msg}`)
        } else {
          // READ failure: skip this step and CONTINUE. A failed read (404, empty
          // index, timeout) must NEVER abort the plan or block later write steps.
          outputs[step.step] = undefined
          setStepStatus(step.step, 'skipped', msg)
          toast.message(`Étape ${step.step} ignorée`, { description: msg })
        }
        // NOTE: no `break` — a single failed step must never abort the whole plan.
      }
    }
    setExecuting(false)
  }

  const reset = () => {
    setResult(null)
    setStatuses({})
    setRanOnce(false)
    setExecuting(false)
    setConfirm(null)
    outputsRef.current = {}
  }

  // ── Render helpers ────────────────────────────────────────────────────────
  const stepLabel = (step: AgentPlanStep) => {
    if (step.label) return step.label
    const meta = opMeta(step.op)
    const a = step.args ?? {}
    const hint =
      (a.query as string) ?? (a.to as string) ?? (a.prompt as string) ?? (a.question as string) ?? ''
    return hint ? `${meta.label} — ${String(hint).slice(0, 60)}` : meta.label
  }

  const StatusIcon = ({ status }: { status?: StepStatus }) => {
    switch (status) {
      case 'running':
        return <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
      case 'confirm':
        return <ShieldCheck className="h-3.5 w-3.5 text-amber-500" />
      case 'done':
        return <Check className="h-3.5 w-3.5 text-green-600" />
      case 'skipped':
        return <X className="h-3.5 w-3.5 text-muted-foreground" />
      case 'error':
        return <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
      default:
        return <CircleDashed className="h-3.5 w-3.5 text-muted-foreground/50" />
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Goal input */}
      <div className="shrink-0 space-y-2 border-b p-3">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-primary">
          <Bot className="h-4 w-4" />
          Mode Agent
        </div>
        <p className="text-xs text-muted-foreground">
          Décrivez un objectif — l’agent propose un plan d’actions que vous validez avant exécution.
        </p>
        <Textarea
          value={goal}
          onChange={(e) => setGoal(e.target.value)}
          placeholder="Ex : « Créer une simulation Monte Carlo de perte opérationnelle et l’enregistrer »"
          className="min-h-[68px] bg-background text-sm"
        />
        <div className="flex items-center gap-2">
          {surface ? (
            <Badge variant="outline" className="max-w-[180px] gap-1 truncate text-[10px]">
              <ClipboardPen className="h-3 w-3" /> {surface.title}
            </Badge>
          ) : (
            <Badge variant="outline" className="text-[10px] text-muted-foreground">
              {module || pageContext || 'page'}
            </Badge>
          )}
          <div className="flex-1" />
          <Button size="sm" onClick={handlePlan} disabled={planM.isPending || !goal.trim()}>
            {planM.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Wand2 className="mr-2 h-4 w-4" />
            )}
            Planifier
          </Button>
        </div>
      </div>

      {/* Plan + execution */}
      <div className="min-h-0 flex-1 overflow-hidden">
        <ScrollArea className="h-full">
          <div className="space-y-3 p-3">
            {!result && !planM.isPending && (
              <div className="py-10 text-center text-xs text-muted-foreground">
                <Bot className="mx-auto mb-2 h-8 w-8 opacity-40" />
                Saisissez un objectif puis « Planifier » pour obtenir un plan d’actions.
              </div>
            )}

            {result?.assistant_message && (
              <div className="rounded-lg border bg-muted/40 p-2.5 text-xs leading-relaxed">
                {result.assistant_message}
              </div>
            )}

            {result && (
              <div className="flex flex-wrap items-center gap-1.5">
                {result.grounded && (
                  <Badge variant="secondary" className="gap-1 text-[10px]">
                    <ShieldCheck className="h-3 w-3 text-green-600" /> Fondé sur vos données
                  </Badge>
                )}
                {result.requires_confirmation && (
                  <Badge variant="outline" className="gap-1 text-[10px] text-amber-600">
                    <ShieldCheck className="h-3 w-3" /> Confirmation requise
                  </Badge>
                )}
                {result.model && (
                  <Badge variant="outline" className="font-mono text-[10px]">
                    {result.model}
                  </Badge>
                )}
              </div>
            )}

            {/* Ordered plan */}
            {result?.plan.map((step) => {
              const meta = opMeta(step.op)
              const StepIcon = meta.icon
              const st = statuses[step.step]?.status
              const isWrite = WRITE_OPS.has(step.op)
              const gated = step.confirm === true || isWrite
              return (
                <div
                  key={step.step}
                  className={cn(
                    'rounded-lg border p-2.5 text-xs',
                    st === 'done' && 'border-green-500/30 bg-green-500/5',
                    st === 'error' && 'border-red-500/30 bg-red-500/5',
                    st === 'running' && 'border-primary/40 bg-primary/5',
                    st === 'skipped' && 'opacity-60',
                  )}
                >
                  <div className="flex items-start gap-2">
                    <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-bold">
                      {step.step}
                    </div>
                    <StepIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                    <div className="min-w-0 flex-1">
                      <p className="font-medium leading-snug">{stepLabel(step)}</p>
                      <div className="mt-1 flex flex-wrap items-center gap-1">
                        <Badge variant="outline" className="font-mono text-[9px]">
                          {step.op}
                        </Badge>
                        {gated && (
                          <Badge variant="outline" className="gap-1 text-[9px] text-amber-600">
                            <ShieldCheck className="h-2.5 w-2.5" /> confirmation
                          </Badge>
                        )}
                      </div>
                      {statuses[step.step]?.error && (
                        <p
                          className={cn(
                            'mt-1 text-[11px]',
                            statuses[step.step]?.status === 'skipped'
                              ? 'text-muted-foreground'
                              : 'text-red-500',
                          )}
                        >
                          {statuses[step.step]?.error}
                        </p>
                      )}
                    </div>
                    <div className="mt-0.5 shrink-0">
                      <StatusIcon status={st} />
                    </div>
                  </div>
                </div>
              )
            })}

            {/* Inline confirm gate (write steps + confirm:true) */}
            {confirm && (
              <div className="rounded-lg border-2 border-amber-400/60 bg-amber-50/60 p-3 dark:bg-amber-950/20">
                <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-amber-700 dark:text-amber-400">
                  <ShieldCheck className="h-4 w-4" />
                  Confirmer l’étape {confirm.step.step} — {opMeta(confirm.step.op).label}
                </div>
                <p className="mb-2 text-[11px] text-muted-foreground">
                  Valeurs proposées avant écriture / ouverture :
                </p>
                <div className="mb-2.5 max-h-40 space-y-1 overflow-auto rounded-md border bg-background p-2">
                  {Object.entries(argValues(confirm.args)).length === 0 ? (
                    <pre className="whitespace-pre-wrap text-[10px] leading-relaxed">
                      {JSON.stringify(confirm.args, null, 2)}
                    </pre>
                  ) : (
                    Object.entries(argValues(confirm.args)).map(([k, v]) => (
                      <div key={k} className="flex items-start gap-2 text-[11px]">
                        <span className="min-w-[90px] shrink-0 font-medium text-muted-foreground">{k}</span>
                        <span className="min-w-0 break-words">
                          {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                        </span>
                      </div>
                    ))
                  )}
                </div>
                <div className="flex items-center justify-end gap-2">
                  <Button size="sm" variant="outline" onClick={() => decideConfirm(false)}>
                    <X className="mr-1.5 h-3.5 w-3.5" /> Ignorer
                  </Button>
                  <Button size="sm" onClick={() => decideConfirm(true)}>
                    <Check className="mr-1.5 h-3.5 w-3.5" /> Confirmer
                  </Button>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Actions */}
      {result && result.plan.length > 0 && (
        <div className="shrink-0 border-t p-3">
          <div className="flex items-center gap-2">
            <Button className="flex-1" onClick={runPlan} disabled={executing || ranOnce}>
              {executing ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Play className="mr-2 h-4 w-4" />
              )}
              {ranOnce ? 'Plan exécuté' : 'Lancer'}
              {!executing && !ranOnce && <ArrowRight className="ml-1.5 h-3.5 w-3.5" />}
            </Button>
            <Button variant="outline" size="icon" onClick={reset} title="Réinitialiser">
              <RotateCcw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export default AgentModePanel
